// Firebase functions for forum operations
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc,
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  serverTimestamp,
  increment,
  writeBatch
} from "firebase/firestore";
import { db, auth, functionsInstance } from "./firebase-config";
import { httpsCallable } from 'firebase/functions';
// Import admin utilities
import { isAdmin } from '../../utils/adminUtils';
// Import storage functions for attachments
import { uploadForumAttachments } from './storage';
// Import mention utilities and services
import { parseMentions, extractMentionedUserIds } from '../../utils/mentionUtils';
import { createMentionNotifications } from './mentionNotifications';
import { updateMentionStats } from './userForumStats';
// Import email logging utility
import EmailLogger from '../../utils/emailLogger';

// Forum notifications are handled automatically by Firestore triggers
// No need for explicit callable functions - notifications are sent when replies/likes are created

// Add a simple test to verify db connection
if (!db) {
  console.error('❌ Firestore database not initialized properly');
} else {
  console.log('✅ Firestore database initialized successfully for forum');
}

/**
 * Data validation functions
 */

// Helper function removed - was unused
// const getTextContent = (text) => {
//   return text || '';
// };

/**
 * Validates question data
 * @param {Object} questionData - The question data to validate
 * @returns {Object} - Validation result with success status and errors
 */
export const validateQuestionData = (questionData) => {
  const errors = [];
  
  // Required fields
  if (!questionData.title || questionData.title.trim() === '') {
    errors.push('Question title is required');
  }
  
  // For content, check plain text content
  if (!questionData.content || questionData.content.trim() === '') {
    errors.push('Question content is required');
  }
  
  if (!questionData.authorId || questionData.authorId.trim() === '') {
    errors.push('Author ID is required');
  }
  
  if (!questionData.authorName || questionData.authorName.trim() === '') {
    errors.push('Author name is required');
  }
  
  // Validate field lengths
  if (questionData.title && questionData.title.length > 200) {
    errors.push('Question title must be 200 characters or less');
  }
  
  // Validate content length for plain text
  if (questionData.content && questionData.content.length > 5000) {
    errors.push('Question content must be 5000 characters or less');
  }
  
  // Validate tags if provided
  if (questionData.tags && !Array.isArray(questionData.tags)) {
    errors.push('Tags must be an array');
  }
  
  if (questionData.tags && questionData.tags.length > 5) {
    errors.push('Maximum 5 tags allowed');
  }
  
  // Validate mentions if provided
  if (questionData.content) {
    const mentions = parseMentions(questionData.content);
    if (mentions.length > 10) {
      errors.push('Maximum 10 mentions allowed per post');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates reply data
 * @param {Object} replyData - The reply data to validate
 * @returns {Object} - Validation result with success status and errors
 */
export const validateReplyData = (replyData) => {
  const errors = [];
  
  // Required fields
  if (!replyData.questionId || replyData.questionId.trim() === '') {
    errors.push('Question ID is required');
  }
  
  // Allow replies with just attachments or just content, but require at least one
  const hasContent = replyData.content && replyData.content.trim() !== '';
  const hasAttachments = replyData.attachments && replyData.attachments.length > 0;
  
  if (!hasContent && !hasAttachments) {
    errors.push('Reply must have either content or attachments');
  }
  
  if (!replyData.authorId || replyData.authorId.trim() === '') {
    errors.push('Author ID is required');
  }
  
  if (!replyData.authorName || replyData.authorName.trim() === '') {
    errors.push('Author name is required');
  }
  
  // Validate field lengths
  if (replyData.content && replyData.content.length > 3000) {
    errors.push('Reply content must be 3000 characters or less');
  }
  
  // Validate attachments if provided
  if (replyData.attachments && replyData.attachments.length > 5) {
    errors.push('Maximum 5 attachments allowed per reply');
  }
  
  // Validate mentions if provided
  if (replyData.content) {
    const mentions = parseMentions(replyData.content);
    if (mentions.length > 10) {
      errors.push('Maximum 10 mentions allowed per reply');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Permission validation functions
 */

/**
 * Check if user has permission to delete content
 * @param {string} contentId - The ID of the content (question or reply)
 * @param {string} userId - The ID of the user requesting deletion
 * @param {string} contentType - 'question' or 'reply'
 * @returns {Promise<Object>} - Permission check result
 */
export const checkDeletionPermissions = async (contentId, userId, contentType) => {
  try {
    // Check if user is authenticated
    if (!userId) {
      return { 
        canDelete: false, 
        error: 'User not authenticated',
        errorCode: 'UNAUTHENTICATED'
      };
    }

    // Validate content type
    if (!['question', 'reply'].includes(contentType)) {
      return { 
        canDelete: false, 
        error: 'Invalid content type',
        errorCode: 'INVALID_CONTENT_TYPE'
      };
    }

    // Get content data
    const collection = contentType === 'question' ? 'forum_questions' : 'forum_replies';
    const contentRef = doc(db, collection, contentId);
    const contentSnap = await getDoc(contentRef);

    if (!contentSnap.exists()) {
      return { 
        canDelete: false, 
        error: `${contentType} not found`,
        errorCode: 'CONTENT_NOT_FOUND'
      };
    }

    const contentData = contentSnap.data();

    // Check if already deleted
    if (contentData.status === 'deleted') {
      return { 
        canDelete: false, 
        error: 'Content already deleted',
        errorCode: 'ALREADY_DELETED'
      };
    }

    // Check if user is admin
    const userIsAdmin = await isAdmin(userId);
    
    // Check if user is author
    const isAuthor = contentData.authorId === userId;

    if (!userIsAdmin && !isAuthor) {
      return { 
        canDelete: false, 
        error: 'Insufficient permissions to delete this content',
        errorCode: 'INSUFFICIENT_PERMISSIONS'
      };
    }

    return { 
      canDelete: true, 
      isAdmin: userIsAdmin, 
      isAuthor, 
      contentData,
      error: null,
      errorCode: null
    };
  } catch (error) {
    console.error('Error checking deletion permissions:', error);
    return { 
      canDelete: false, 
      error: error.message || 'Failed to check permissions',
      errorCode: 'PERMISSION_CHECK_FAILED'
    };
  }
};

/**
 * Validate deletion request with comprehensive checks
 * @param {string} contentId - The ID of the content
 * @param {string} userId - The ID of the user
 * @param {string} contentType - 'question' or 'reply'
 * @param {string} reason - Deletion reason (required for admin deletions)
 * @returns {Promise<Object>} - Validation result
 */
export const validateDeletionRequest = async (contentId, userId, contentType, reason = '') => {
  try {
    // Basic parameter validation
    if (!contentId || !userId || !contentType) {
      return {
        isValid: false,
        error: 'Missing required parameters',
        errorCode: 'MISSING_PARAMETERS'
      };
    }

    // Check deletion permissions
    const permissionCheck = await checkDeletionPermissions(contentId, userId, contentType);
    
    if (!permissionCheck.canDelete) {
      return {
        isValid: false,
        error: permissionCheck.error,
        errorCode: permissionCheck.errorCode
      };
    }

    // If admin is deleting someone else's content, reason is required
    if (permissionCheck.isAdmin && !permissionCheck.isAuthor && !reason.trim()) {
      return {
        isValid: false,
        error: 'Deletion reason is required for admin moderation',
        errorCode: 'REASON_REQUIRED'
      };
    }

    // Validate reason length if provided
    if (reason && reason.length > 500) {
      return {
        isValid: false,
        error: 'Deletion reason must be 500 characters or less',
        errorCode: 'REASON_TOO_LONG'
      };
    }

    return {
      isValid: true,
      permissionData: permissionCheck,
      error: null,
      errorCode: null
    };
  } catch (error) {
    console.error('Error validating deletion request:', error);
    return {
      isValid: false,
      error: error.message || 'Failed to validate deletion request',
      errorCode: 'VALIDATION_FAILED'
    };
  }
};

/**
 * Check if user can perform any admin actions
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} - True if user has admin privileges
 */
export const checkAdminPermissions = async (userId) => {
  if (!userId) {
    return false;
  }

  try {
    return await isAdmin(userId);
  } catch (error) {
    console.error('Error checking admin permissions:', error);
    return false;
  }
};

/**
 * Batch check deletion permissions for multiple items
 * @param {Array} items - Array of {contentId, contentType} objects
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Object mapping contentId to permission result
 */
export const batchCheckDeletionPermissions = async (items, userId) => {
  if (!Array.isArray(items) || items.length === 0 || !userId) {
    return {};
  }

  try {
    const results = {};
    
    // Check admin status once for efficiency
    const userIsAdmin = await isAdmin(userId);
    
    // Process each item
    const promises = items.map(async ({ contentId, contentType }) => {
      try {
        const collection = contentType === 'question' ? 'forum_questions' : 'forum_replies';
        const contentRef = doc(db, collection, contentId);
        const contentSnap = await getDoc(contentRef);

        if (!contentSnap.exists()) {
          return {
            contentId,
            canDelete: false,
            error: `${contentType} not found`,
            errorCode: 'CONTENT_NOT_FOUND'
          };
        }

        const contentData = contentSnap.data();
        const isAuthor = contentData.authorId === userId;
        const canDelete = userIsAdmin || isAuthor;

        return {
          contentId,
          canDelete,
          isAdmin: userIsAdmin,
          isAuthor,
          error: canDelete ? null : 'Insufficient permissions',
          errorCode: canDelete ? null : 'INSUFFICIENT_PERMISSIONS'
        };
      } catch (error) {
        return {
          contentId,
          canDelete: false,
          error: error.message,
          errorCode: 'CHECK_FAILED'
        };
      }
    });

    const permissionResults = await Promise.all(promises);
    
    // Convert to object mapping
    for (const result of permissionResults) {
      results[result.contentId] = result;
    }

    return results;
  } catch (error) {
    console.error('Error in batch permission check:', error);
    return {};
  }
};

/**
 * Helper functions for deletion operations
 */

/**
 * Update engagement metrics when content is deleted
 * @param {string} contentId - The ID of the content
 * @param {string} contentType - 'question' or 'reply'
 * @param {Object} contentData - The content data
 * @param {Object} batch - Firestore batch object
 */
const updateEngagementMetricsForDeletion = async (contentId, contentType, contentData, batch) => {
  try {
    // Update user forum stats
    if (contentData.authorId) {
      const userStatsRef = doc(db, 'user_forum_stats', contentData.authorId);
      
      if (contentType === 'question') {
        batch.set(userStatsRef, {
          questionsPosted: increment(-1),
          totalLikesReceived: increment(-(contentData.likeCount || 0)),
          updatedAt: serverTimestamp()
        }, { merge: true });
      } else if (contentType === 'reply') {
        batch.set(userStatsRef, {
          repliesPosted: increment(-1),
          totalLikesReceived: increment(-(contentData.likeCount || 0)),
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    }

    // For replies, update parent question reply count
    if (contentType === 'reply' && contentData.questionId) {
      const questionRef = doc(db, 'forum_questions', contentData.questionId);
      batch.update(questionRef, {
        replyCount: increment(-1),
        updatedAt: serverTimestamp()
      });
    }

    // Remove likes associated with the deleted content
    if (contentData.likeCount > 0) {
      // Note: In a production system, you might want to batch delete likes
      // For now, we'll handle this in a separate operation
      setTimeout(async () => {
        try {
          const likesQuery = query(
            collection(db, 'forum_likes'),
            where('targetId', '==', contentId),
            where('targetType', '==', contentType)
          );
          
          const likesSnapshot = await getDocs(likesQuery);
          const deleteBatch = writeBatch(db);
          
          likesSnapshot.docs.forEach(doc => {
            deleteBatch.delete(doc.ref);
          });
          
          if (likesSnapshot.docs.length > 0) {
            await deleteBatch.commit();
          }
        } catch (error) {
          console.error('Error cleaning up likes for deleted content:', error);
        }
      }, 1000); // Delay to avoid transaction conflicts
    }
  } catch (error) {
    console.error('Error updating engagement metrics for deletion:', error);
    // Don't throw error to avoid breaking the main deletion operation
  }
};

/**
 * Log deletion action for audit trail
 * @param {Object} logData - The deletion log data
 */
const logDeletionAction = async (logData) => {
  try {
    // Import the logging service
    const { logDeletionAction: logDeletion } = await import('./deletion-logs');
    
    const result = await logDeletion(logData);
    
    if (!result.success) {
      console.error('Failed to log deletion action:', result.error);
    }
  } catch (error) {
    console.error('Error logging deletion action:', error);
    // Don't throw error to avoid breaking the main deletion operation
  }
};

/**
 * CRUD operations for forum questions
 */

/**
 * Create a new forum question
 * @param {Object} questionData - The question data
 * @returns {Promise<Object>} - Success status and created question data or error
 */
export const createQuestion = async (questionData) => {
  try {
    console.log('Creating forum question:', questionData);
    
    // Validate data
    const validation = validateQuestionData(questionData);
    if (!validation.isValid) {
      return { 
        success: false, 
        error: 'Validation failed', 
        validationErrors: validation.errors 
      };
    }
    
    // Process mentions in content
    const mentions = parseMentions(questionData.content);
    const mentionedUserIds = extractMentionedUserIds(mentions);
    
    // Prepare question data with defaults
    const questionToCreate = {
      title: questionData.title.trim(),
      content: questionData.content.trim(),
      authorId: questionData.authorId,
      authorName: questionData.authorName,
      authorPhotoURL: questionData.authorPhotoURL || '',
      tags: questionData.tags || [],
      
      // Mention data
      mentions: mentions,
      mentionedUserIds: mentionedUserIds,
      hasMentions: mentions.length > 0,
      
      // Engagement metrics
      viewCount: 0,
      replyCount: 0,
      likeCount: 0,
      
      // Status and moderation
      status: 'active',
      isSticky: false,
      moderatedBy: null,
      moderatedAt: null,
      
      // Search optimization
      searchKeywords: generateSearchKeywords(questionData.title, questionData.content),
      category: 'general',
      
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, 'forum_questions'), questionToCreate);
    
    // Update user forum stats
    await updateUserForumStats(questionData.authorId, { questionsPosted: increment(1) });
    
    // Create mention notifications if there are mentions
    if (mentions.length > 0) {
      EmailLogger.trigger('User mentions detected in question', {
        questionId: docRef.id,
        title: questionData.title,
        authorName: questionData.authorName,
        mentionCount: mentions.length,
        content: questionData.content.substring(0, 100) + '...'
      });
      
      try {
        const contentData = {
          id: docRef.id,
          type: 'question',
          title: questionData.title,
          content: questionData.content,
          authorId: questionData.authorId,
          authorName: questionData.authorName,
          authorPhotoURL: questionData.authorPhotoURL || ''
        };
        
        await createMentionNotifications(mentions, contentData);
        await updateMentionStats(questionData.authorId, mentionedUserIds);
        
        console.log(`Created ${mentions.length} mention notifications for question ${docRef.id}`);
        EmailLogger.success(`Created ${mentions.length} mention notifications for question`, {
          questionId: docRef.id,
          mentionCount: mentions.length
        });
      } catch (mentionError) {
        console.error('Error creating mention notifications:', mentionError);
        EmailLogger.error('Failed to create mention notifications for question', {
          error: mentionError.message,
          questionId: docRef.id,
          mentionCount: mentions.length
        });
        // Don't fail the entire operation if mention notifications fail
      }
    } else {
      EmailLogger.skip('No user mentions found in question content', {
        questionId: docRef.id
      });
    }
    
    return {
      success: true,
      data: {
        id: docRef.id,
        ...questionToCreate
      }
    };
  } catch (error) {
    console.error('Error creating forum question:', error);
    return {
      success: false,
      error: error.message || 'Failed to create forum question'
    };
  }
};

/**
 * Get forum questions with pagination and filtering
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and questions data or error
 */
export const getQuestions = async (options = {}) => {
  try {
    console.log('🔥 getQuestions called with options:', options);
    
    const {
      limit: limitCount = 20,
      lastVisible = null,
      sortBy = 'recent', // recent, popular, mostReplies
      searchTerm = null,
      tags = [],
      status = 'active',
      authorId = null // For "My Posts" filtering
    } = options;
    
    let questionsQuery = collection(db, 'forum_questions');
    
    // Build query constraints
    const queryConstraints = [];
    
    // Add status filter
    if (status) {
      queryConstraints.push(where('status', '==', status));
    }
    
    // Add tags filter
    if (tags.length > 0) {
      queryConstraints.push(where('tags', 'array-contains-any', tags));
    }
    
    // Add author filter for "My Posts"
    if (authorId) {
      queryConstraints.push(where('authorId', '==', authorId));
    }
    
    // Add ordering based on sortBy
    switch (sortBy) {
      case 'popular':
        queryConstraints.push(orderBy('likeCount', 'desc'));
        queryConstraints.push(orderBy('createdAt', 'desc')); // Secondary sort
        break;
      case 'mostReplies':
        queryConstraints.push(orderBy('replyCount', 'desc'));
        queryConstraints.push(orderBy('createdAt', 'desc')); // Secondary sort
        break;
      case 'recent':
      default:
        queryConstraints.push(orderBy('createdAt', 'desc'));
        break;
    }
    
    // Add pagination
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    // Add limit (increase for client-side filtering)
    const queryLimit = searchTerm ? Math.max(limitCount * 2, 50) : limitCount;
    queryConstraints.push(limit(queryLimit));
    
    // Apply constraints
    questionsQuery = query(questionsQuery, ...queryConstraints);
    
    // Execute query
    console.log('🔍 Executing Firestore query...');
    const snapshot = await getDocs(questionsQuery);
    console.log('📋 Query result - docs found:', snapshot.docs.length);
    
    if (snapshot.empty) {
      console.log('📭 No questions found in database');
      return {
        success: true,
        data: {
          questions: [],
          lastVisible: null,
          hasMore: false
        }
      };
    }
    
    // Transform data
    let questions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Apply client-side search filtering if search term is provided
    if (searchTerm && searchTerm.trim()) {
      questions = applySearchFilter(questions, searchTerm.trim());
    }
    
    // Get last document for pagination (before potential trimming)
    const lastDoc = snapshot.docs[snapshot.docs.length - 1];
    
    // Trim results to requested limit if we fetched more for search
    if (searchTerm && questions.length > limitCount) {
      questions = questions.slice(0, limitCount);
    }

    return {
      success: true,
      data: {
        questions,
        lastVisible: lastDoc,
        hasMore: snapshot.docs.length === queryLimit && (!searchTerm || questions.length === limitCount)
      }
    };
  } catch (error) {
    console.error('Error fetching forum questions:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch forum questions'
    };
  }
};

/**
 * Update an existing forum question
 * @param {string} questionId - The ID of the question to update
 * @param {Object} updateData - The data to update
 * @param {string} userId - The ID of the user making the update
 * @returns {Promise<Object>} - Success status and updated question data or error
 */
export const updateQuestion = async (questionId, updateData, userId) => {
  try {
    console.log('Updating forum question:', questionId, updateData);
    
    if (!questionId || !userId) {
      return { success: false, error: 'Question ID and user ID are required' };
    }
    
    // Get the existing question to verify ownership
    const questionRef = doc(db, 'forum_questions', questionId);
    const questionSnap = await getDoc(questionRef);
    
    if (!questionSnap.exists()) {
      return { success: false, error: 'Question not found' };
    }
    
    const existingQuestion = questionSnap.data();
    
    // Check if user is the author or an admin
    if (existingQuestion.authorId !== userId) {
      // TODO: Add admin check when admin roles are implemented
      return { success: false, error: 'You can only edit your own questions' };
    }
    
    // Prepare update data with validation
    const allowedFields = ['title', 'content', 'tags'];
    const filteredUpdateData = {};
    
    Object.keys(updateData).forEach(key => {
      if (allowedFields.includes(key) && updateData[key] !== undefined) {
        filteredUpdateData[key] = updateData[key];
      }
    });
    
    // Validate the update data
    const dataToValidate = {
      ...existingQuestion,
      ...filteredUpdateData,
      authorId: existingQuestion.authorId,
      authorName: existingQuestion.authorName
    };
    
    const validation = validateQuestionData(dataToValidate);
    if (!validation.isValid) {
      return { 
        success: false, 
        error: 'Validation failed', 
        validationErrors: validation.errors 
      };
    }
    
    // Add metadata
    filteredUpdateData.updatedAt = serverTimestamp();
    
    // Update search keywords if title or content changed
    if (filteredUpdateData.title || filteredUpdateData.content) {
      const newTitle = filteredUpdateData.title || existingQuestion.title;
      const newContent = filteredUpdateData.content || existingQuestion.content;
      filteredUpdateData.searchKeywords = generateSearchKeywords(newTitle, newContent);
    }
    
    // Update the question
    await updateDoc(questionRef, filteredUpdateData);
    
    return {
      success: true,
      data: {
        id: questionId,
        ...existingQuestion,
        ...filteredUpdateData
      }
    };
  } catch (error) {
    console.error('Error updating forum question:', error);
    return {
      success: false,
      error: error.message || 'Failed to update forum question'
    };
  }
};

/**
 * Update an existing forum reply
 * @param {string} replyId - The ID of the reply to update
 * @param {Object} updateData - The data to update
 * @param {string} userId - The ID of the user making the update
 * @returns {Promise<Object>} - Success status and updated reply data or error
 */
export const updateReply = async (replyId, updateData, userId) => {
  try {
    console.log('Updating forum reply:', replyId, updateData);
    
    if (!replyId || !userId) {
      return { success: false, error: 'Reply ID and user ID are required' };
    }
    
    // Get the existing reply to verify ownership
    const replyRef = doc(db, 'forum_replies', replyId);
    const replySnap = await getDoc(replyRef);
    
    if (!replySnap.exists()) {
      return { success: false, error: 'Reply not found' };
    }
    
    const existingReply = replySnap.data();
    
    // Check if user is the author or an admin
    if (existingReply.authorId !== userId) {
      // Check if user is admin
      const userIsAdmin = await isAdmin(userId);
      if (!userIsAdmin) {
        return { success: false, error: 'You can only edit your own replies' };
      }
    }
    
    // Prepare update data with validation
    const allowedFields = ['content'];
    const filteredUpdateData = {};
    
    Object.keys(updateData).forEach(key => {
      if (allowedFields.includes(key) && updateData[key] !== undefined) {
        filteredUpdateData[key] = updateData[key];
      }
    });
    
    // Validate the update data
    const dataToValidate = {
      ...existingReply,
      ...filteredUpdateData,
      authorId: existingReply.authorId,
      authorName: existingReply.authorName,
      questionId: existingReply.questionId
    };
    
    const validation = validateReplyData(dataToValidate);
    if (!validation.isValid) {
      return { 
        success: false, 
        error: 'Validation failed', 
        validationErrors: validation.errors 
      };
    }
    
    // Add metadata
    filteredUpdateData.updatedAt = serverTimestamp();
    
    // Update the reply
    await updateDoc(replyRef, filteredUpdateData);
    
    return {
      success: true,
      data: {
        id: replyId,
        ...existingReply,
        ...filteredUpdateData
      }
    };
  } catch (error) {
    console.error('Error updating forum reply:', error);
    return {
      success: false,
      error: error.message || 'Failed to update forum reply'
    };
  }
};

/**
 * Get a specific forum question by ID and increment view count
 * @param {string} questionId - The ID of the question
 * @param {string} userId - The ID of the viewing user (optional, for view tracking)
 * @returns {Promise<Object>} - Success status and question data or error
 */
export const getQuestionById = async (questionId, userId = null) => {
  try {
    console.log('Fetching forum question:', questionId);
    
    if (!questionId) {
      return { success: false, error: 'Question ID is required' };
    }
    
    const docRef = doc(db, 'forum_questions', questionId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return { success: false, error: 'Forum question not found' };
    }
    
    const questionData = {
      id: docSnap.id,
      ...docSnap.data()
    };
    
    // Check if question is deleted and return appropriate error
    if (questionData.status === 'deleted') {
      return { 
        success: false, 
        error: 'This post has been deleted and is no longer available',
        errorCode: 'QUESTION_DELETED'
      };
    }
    
    // Increment view count (don't increment for the author viewing their own question)
    if (userId && userId !== questionData.authorId) {
      try {
        await updateDoc(docRef, {
          viewCount: increment(1),
          updatedAt: serverTimestamp()
        });
        questionData.viewCount = (questionData.viewCount || 0) + 1;
      } catch (error) {
        console.warn('Failed to increment view count:', error);
        // Don't fail the entire request if view count update fails
      }
    }
    
    return {
      success: true,
      data: questionData
    };
  } catch (error) {
    console.error('Error fetching forum question:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch forum question'
    };
  }
};

/**
 * CRUD operations for forum replies
 */

/**
 * Create a new forum reply
 * @param {Object} replyData - The reply data
 * @returns {Promise<Object>} - Success status and created reply data or error
 */
export const createReply = async (replyData) => {
  try {
    console.log('Creating forum reply:', replyData);
    console.log('Current user auth state:', auth.currentUser);
    
    // Validate data
    const validation = validateReplyData(replyData);
    if (!validation.isValid) {
      return { 
        success: false, 
        error: 'Validation failed', 
        validationErrors: validation.errors 
      };
    }
    
    // Check if the question exists
    const questionRef = doc(db, 'forum_questions', replyData.questionId);
    const questionSnap = await getDoc(questionRef);
    
    if (!questionSnap.exists()) {
      return { success: false, error: 'Question not found' };
    }

    // Generate temporary reply ID for file uploads
    const tempReplyRef = doc(collection(db, 'forum_replies'));
    const replyId = tempReplyRef.id;
    
    // Handle file attachments if present
    let attachmentInfo = null;
    if (replyData.attachments && replyData.attachments.length > 0) {
      try {
        console.log(`Uploading ${replyData.attachments.length} attachments for reply...`);
        const uploadResult = await uploadForumAttachments(replyData.authorId, replyData.attachments, replyId);
        
        if (uploadResult.success && uploadResult.attachments.length > 0) {
          attachmentInfo = {
            count: uploadResult.attachments.length,
            files: uploadResult.attachments,
            totalSize: uploadResult.attachments.reduce((sum, file) => sum + file.size, 0)
          };
          console.log('Attachments uploaded successfully:', attachmentInfo);
        }
        
        if (uploadResult.failedUploads && uploadResult.failedUploads.length > 0) {
          console.warn('Some attachments failed to upload:', uploadResult.failedUploads);
        }
      } catch (uploadError) {
        console.error('Attachment upload failed:', uploadError);
        return {
          success: false,
          error: 'Failed to upload attachments. Please try again.'
        };
      }
    }
    
    // Process mentions in content
    const mentions = parseMentions(replyData.content || '');
    const mentionedUserIds = extractMentionedUserIds(mentions);
    
    // Get question data for mention notifications
    const questionData = questionSnap.data();
    
    // Prepare reply data with defaults
    const replyToCreate = {
      questionId: replyData.questionId,
      content: replyData.content ? replyData.content.trim() : '',
      authorId: replyData.authorId,
      authorName: replyData.authorName,
      authorPhotoURL: replyData.authorPhotoURL || '',
      
      // Mention data
      mentions: mentions,
      mentionedUserIds: mentionedUserIds,
      hasMentions: mentions.length > 0,
      
      // Attachments
      attachments: attachmentInfo,
      hasAttachments: !!attachmentInfo,
      
      // Engagement metrics
      likeCount: 0,
      
      // Status and moderation
      status: 'active',
      moderatedBy: null,
      moderatedAt: null,
      
      // Threading (for future nested replies)
      parentReplyId: null,
      depth: 0,
      
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Use batch write to create reply and update question reply count
    const batch = writeBatch(db);
    
    // Add reply with the pre-generated ID
    batch.set(tempReplyRef, replyToCreate);
    
    // Update question reply count
    batch.update(questionRef, {
      replyCount: increment(1),
      updatedAt: serverTimestamp()
    });
    
    // Commit batch - this is the only critical operation that needs to complete
    await batch.commit();
    
    // Return immediately for faster UI response
    const replyResult = {
      id: replyId,
      ...replyToCreate,
      // Add client-side timestamp for immediate display
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Process non-critical operations in background (don't await)
    processReplyBackgroundTasks(replyData, mentions, mentionedUserIds, questionData, replyId)
      .catch(error => {
        console.error('Background reply processing failed:', error);
        // Log but don't fail the main operation
      });
    
    return {
      success: true,
      data: replyResult
    };
  } catch (error) {
    console.error('Error creating forum reply:', error);
    return {
      success: false,
      error: error.message || 'Failed to create forum reply'
    };
  }
};

/**
 * Process non-critical reply operations in background
 * @param {Object} replyData - Reply data
 * @param {Array} mentions - Mention objects
 * @param {Array} mentionedUserIds - User IDs that were mentioned
 * @param {Object} questionData - Question data
 * @param {string} replyId - Reply ID
 * @returns {Promise<void>}
 */
const processReplyBackgroundTasks = async (replyData, mentions, mentionedUserIds, questionData, replyId) => {
  try {
    // Update user forum stats (non-critical)
    await updateUserForumStats(replyData.authorId, { repliesPosted: increment(1) });
    
    // Send reply notification to question author (if not replying to own question)
    if (questionData.authorId !== replyData.authorId) {
      EmailLogger.trigger('Reply notification to question author', {
        recipientId: questionData.authorId,
        questionTitle: questionData.title,
        actorName: replyData.authorName,
        questionId: replyData.questionId,
        replyId: replyId
      });
      
      // Reply notifications are handled automatically by Firestore triggers (onReplyCreatedWithPreferences)
      EmailLogger.info('Reply notification will be handled by Firestore trigger', {
        recipientId: questionData.authorId,
        replyId: replyId,
        questionId: replyData.questionId
      });
    } else {
      EmailLogger.skip('Skipping reply notification (replying to own question)', {
        authorId: replyData.authorId,
        questionId: replyData.questionId
      });
    }
    
    // Process mention notifications if there are mentions
    if (mentions.length > 0) {
      EmailLogger.trigger('User mentions detected in reply', {
        replyId: replyId,
        questionId: replyData.questionId,
        questionTitle: questionData.title,
        authorName: replyData.authorName,
        mentionCount: mentions.length
      });
      
      try {
        const contentData = {
          id: replyId,
          type: 'reply',
          questionId: replyData.questionId,
          questionTitle: questionData.title,
          content: replyData.content,
          authorId: replyData.authorId,
          authorName: replyData.authorName,
          authorPhotoURL: replyData.authorPhotoURL || ''
        };
        
        // Create mention notifications (can be slow due to duplicate checks)
        await createMentionNotifications(mentions, contentData);
        
        // Update mention stats
        await updateMentionStats(replyData.authorId, mentionedUserIds);
        
        console.log(`Created ${mentions.length} mention notifications for reply ${replyId}`);
      } catch (mentionError) {
        console.error('Error creating mention notifications:', mentionError);
        // Don't fail the entire background operation
      }
    } else {
      EmailLogger.skip('No user mentions found in reply content', {
        replyId: replyId
      });
    }
    
    console.log(`Background processing completed for reply ${replyId}`);
  } catch (error) {
    console.error('Background reply processing error:', error);
    EmailLogger.error('Background reply processing failed', {
      error: error.message,
      replyId: replyId
    });
    // Background operations failing shouldn't affect user experience
  }
};

/**
 * Get replies for a specific question
 * @param {string} questionId - The ID of the question
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and replies data or error
 */
export const getReplies = async (questionId, options = {}) => {
  try {
    console.log('Fetching replies for question:', questionId);
    
    if (!questionId) {
      return { success: false, error: 'Question ID is required' };
    }
    
    const {
      limit: limitCount = 50,
      lastVisible = null,
      orderDirection = 'asc' // Chronological order for replies
    } = options;
    
    let repliesQuery = collection(db, 'forum_replies');
    
    // Build query constraints
    const queryConstraints = [
      where('questionId', '==', questionId),
      where('status', '==', 'active'),
      orderBy('createdAt', orderDirection)
    ];
    
    // Add pagination
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    // Add limit
    queryConstraints.push(limit(limitCount));
    
    // Apply constraints
    repliesQuery = query(repliesQuery, ...queryConstraints);
    
    // Execute query
    const snapshot = await getDocs(repliesQuery);
    
    if (snapshot.empty) {
      return {
        success: true,
        data: {
          replies: [],
          lastVisible: null,
          hasMore: false
        }
      };
    }
    
    // Get last document for pagination
    const lastDoc = snapshot.docs[snapshot.docs.length - 1];
    
    // Transform data
    const replies = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return {
      success: true,
      data: {
        replies,
        lastVisible: lastDoc,
        hasMore: replies.length === limitCount
      }
    };
  } catch (error) {
    console.error('Error fetching forum replies:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch forum replies'
    };
  }
};

/**
 * Like/Unlike operations
 */

/**
 * Toggle like for a question or reply
 * @param {string} userId - The ID of the user
 * @param {string} targetType - 'question' or 'reply'
 * @param {string} targetId - The ID of the question or reply
 * @returns {Promise<Object>} - Success status and like data or error
 */
export const toggleLike = async (userId, targetType, targetId) => {
  try {
    console.log('🔥 DEBUG: Starting toggleLike:', { userId, targetType, targetId });
    console.log('🔥 DEBUG: Auth user:', auth.currentUser ? {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email
    } : 'No auth user');
    
    if (!userId || !targetType || !targetId) {
      console.log('🔥 DEBUG: Missing parameters');
      return { success: false, error: 'Missing required parameters' };
    }
    
    if (!['question', 'reply'].includes(targetType)) {
      console.log('🔥 DEBUG: Invalid target type:', targetType);
      return { success: false, error: 'Invalid target type' };
    }
    
    // Use composite document ID for instant lookup instead of slow query
    const likeId = `${userId}_${targetType}_${targetId}`;
    const likeRef = doc(db, 'forum_likes', likeId);
    
    console.log('🔥 DEBUG: Checking for existing like with ID:', likeId);
    const existingLikeSnap = await getDoc(likeRef);
    const existingLike = existingLikeSnap.exists();
    console.log('🔥 DEBUG: Existing like:', existingLike ? 'Found' : 'Not found');
    
    // Get target document reference
    const targetCollection = targetType === 'question' ? 'forum_questions' : 'forum_replies';
    const targetRef = doc(db, targetCollection, targetId);
    console.log('🔥 DEBUG: Target collection:', targetCollection);
    
    // Check if target exists
    const targetSnap = await getDoc(targetRef);
    if (!targetSnap.exists()) {
      console.log('🔥 DEBUG: Target not found');
      return { success: false, error: `${targetType} not found` };
    }
    
    const targetData = targetSnap.data();
    console.log('🔥 DEBUG: Target data found, authorId:', targetData.authorId);
    
    // Use batch write for atomic operations
    const batch = writeBatch(db);
    console.log('🔥 DEBUG: Created batch');
    
    if (existingLike) {
      console.log('🔥 DEBUG: Attempting to unlike...');
      // Unlike: Remove like and decrement count
      batch.delete(likeRef);
      batch.update(targetRef, {
        likeCount: increment(-1),
        updatedAt: serverTimestamp()
      });
      
      console.log('🔥 DEBUG: About to commit unlike batch...');
      await batch.commit();
      console.log('🔥 DEBUG: Unlike batch committed successfully');
      
      return {
        success: true,
        data: {
          action: 'unliked',
          likeCount: Math.max((targetData.likeCount || 0) - 1, 0)
        }
      };
    } else {
      console.log('🔥 DEBUG: Attempting to like...');
      // Like: Add like and increment count using the composite ID
      const likeData = {
        userId,
        targetType,
        targetId,
        createdAt: serverTimestamp()
      };
      
      console.log('🔥 DEBUG: Like data prepared:', likeData);
      console.log('🔥 DEBUG: Like ref ID:', likeId);
      
      batch.set(likeRef, likeData);
      batch.update(targetRef, {
        likeCount: increment(1),
        updatedAt: serverTimestamp()
      });
      
      console.log('🔥 DEBUG: About to commit like batch...');
      await batch.commit();
      console.log('🔥 DEBUG: Like batch committed successfully');
      
      // Update target author's forum stats
      console.log('🔥 DEBUG: Updating forum stats for author:', targetData.authorId);
      await updateUserForumStats(targetData.authorId, { likesReceived: increment(1) });
      console.log('🔥 DEBUG: Forum stats updated successfully');
      
      // Send like notification to content author (if not liking own content)
      if (targetData.authorId !== userId) {
        // Get user name for notification (we need this for the email)
        const currentUser = auth.currentUser;
        const userName = currentUser?.displayName || 'Someone';
        
        EmailLogger.trigger('Like notification to content author', {
          recipientId: targetData.authorId,
          contentType: targetType,
          actorName: userName,
          targetId: targetId
        });
        
        // Get question title for notification (declare outside try block for scope)
        let questionTitle = '';
        if (targetType === 'reply') {
          // Get question title for reply likes
          const questionDoc = await getDoc(doc(db, 'forum_questions', targetData.questionId));
          if (questionDoc.exists()) {
            questionTitle = questionDoc.data().title;
          }
        } else {
          questionTitle = targetData.title;
        }
        
        // Like notifications are handled automatically by Firestore triggers (onLikeCreatedWithPreferences)
        EmailLogger.info('Like notification will be handled by Firestore trigger', {
          recipientId: targetData.authorId,
          likeId: likeId,
          targetType: targetType,
          targetId: targetId
        });
      } else {
        EmailLogger.skip('Skipping like notification (liking own content)', {
          userId: userId,
          targetType: targetType,
          targetId: targetId
        });
      }
      
      return {
        success: true,
        data: {
          action: 'liked',
          likeCount: (targetData.likeCount || 0) + 1,
          likeId: likeId
        }
      };
    }
  } catch (error) {
    console.error('🔥 DEBUG: Error toggling like:', error);
    console.error('🔥 DEBUG: Error code:', error.code);
    console.error('🔥 DEBUG: Error message:', error.message);
    console.error('🔥 DEBUG: Full error:', error);
    return {
      success: false,
      error: error.message || 'Failed to toggle like'
    };
  }
};

/**
 * Get user's likes for questions/replies using optimized direct lookups
 * @param {string} userId - The ID of the user
 * @param {Array} targets - Array of {id, type} objects to check
 * @returns {Promise<Object>} - Success status and likes data or error
 */
export const getUserLikes = async (userId, targets = []) => {
  try {
    if (!userId || targets.length === 0) {
      return { success: true, data: {} };
    }
    
    // Use direct document lookups instead of queries for much better performance
    const likePromises = targets.map(async ({ id: targetId, type: targetType }) => {
      const likeId = `${userId}_${targetType}_${targetId}`;
      const likeRef = doc(db, 'forum_likes', likeId);
      const likeSnap = await getDoc(likeRef);
      
      if (likeSnap.exists()) {
        const data = likeSnap.data();
        return {
          targetId,
          like: {
            id: likeId,
            targetType: data.targetType,
            createdAt: data.createdAt
          }
        };
      }
      return { targetId, like: null };
    });
    
    const results = await Promise.all(likePromises);
    
    const likes = {};
    results.forEach(({ targetId, like }) => {
      if (like) {
        likes[targetId] = like;
      }
    });
    
    return {
      success: true,
      data: likes
    };
  } catch (error) {
    console.error('Error fetching user likes:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch user likes'
    };
  }
};

/**
 * Check if user has liked a specific target (optimized single check)
 * @param {string} userId - The ID of the user
 * @param {string} targetType - 'question' or 'reply'
 * @param {string} targetId - The ID of the target
 * @returns {Promise<Object>} - Success status and like status
 */
export const checkUserLike = async (userId, targetType, targetId) => {
  try {
    if (!userId || !targetType || !targetId) {
      return { success: true, data: { hasLiked: false } };
    }
    
    const likeId = `${userId}_${targetType}_${targetId}`;
    const likeRef = doc(db, 'forum_likes', likeId);
    const likeSnap = await getDoc(likeRef);
    
    return {
      success: true,
      data: {
        hasLiked: likeSnap.exists(),
        likeId: likeSnap.exists() ? likeId : null
      }
    };
  } catch (error) {
    console.error('Error checking user like:', error);
    return {
      success: false,
      error: error.message || 'Failed to check user like'
    };
  }
};

/**
 * Get user's forum activity including questions, replies, and stats
 * @param {string} userId - The ID of the user
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of recent activities to return (default: 5)
 * @returns {Promise<Object>} - Success status and activity data or error
 * @example
 * const result = await getUserForumActivity('user123', { limit: 5 });
 * if (result.success) {
 *   const { stats, recentActivity } = result.data;
 *   console.log(`User has ${stats.totalQuestions} questions and ${stats.totalReplies} replies`);
 * }
 */
export const getUserForumActivity = async (userId, options = {}) => {
  try {
    console.log('Fetching forum activity for user:', userId);
    
    // Validate userId parameter
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      return { 
        success: false, 
        error: 'Valid user ID is required',
        errorCode: 'INVALID_USER_ID'
      };
    }
    
    const { limit: limitCount = 5 } = options;
    
    // Validate limit parameter
    if (typeof limitCount !== 'number' || limitCount < 1 || limitCount > 50) {
      return {
        success: false,
        error: 'Limit must be a number between 1 and 50',
        errorCode: 'INVALID_LIMIT'
      };
    }
    
    // Build queries for parallel execution
    const questionsQuery = query(
      collection(db, 'forum_questions'),
      where('authorId', '==', userId),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const repliesQuery = query(
      collection(db, 'forum_replies'),
      where('authorId', '==', userId),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    // Execute queries in parallel for better performance
    const [questionsSnapshot, repliesSnapshot] = await Promise.all([
      getDocs(questionsQuery),
      getDocs(repliesQuery)
    ]);
    
    // Transform questions data
    const questions = questionsSnapshot.docs.map(doc => ({
      id: doc.id,
      type: 'question',
      ...doc.data()
    }));
    
    // Transform replies data
    const replies = repliesSnapshot.docs.map(doc => ({
      id: doc.id,
      type: 'reply',
      ...doc.data()
    }));
    
    // Calculate comprehensive stats
    const totalQuestions = questionsSnapshot.size;
    const totalReplies = repliesSnapshot.size;
    const totalLikesReceived = questions.reduce((sum, q) => sum + (q.likeCount || 0), 0) +
                              replies.reduce((sum, r) => sum + (r.likeCount || 0), 0);
    
    // Combine and sort by creation date for chronological activity feed
    const allActivity = [...questions, ...replies]
      .sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return bTime - aTime;
      })
      .slice(0, limitCount);
    
    const stats = {
      totalQuestions,
      totalReplies,
      totalActivity: totalQuestions + totalReplies,
      totalLikesReceived
    };
    
    console.log(`Forum activity fetched for user ${userId}: ${totalQuestions + totalReplies} activities`);
    
    return {
      success: true,
      data: {
        stats,
        recentActivity: allActivity
      }
    };
  } catch (error) {
    console.error('Error fetching user forum activity:', error);
    
    // Provide specific error handling for common issues
    let errorMessage = 'Failed to fetch forum activity';
    let errorCode = 'FETCH_FAILED';
    
    if (error.code === 'permission-denied') {
      errorMessage = 'Permission denied accessing forum data';
      errorCode = 'PERMISSION_DENIED';
    } else if (error.code === 'unavailable') {
      errorMessage = 'Forum service temporarily unavailable';
      errorCode = 'SERVICE_UNAVAILABLE';
    } else if (error.message?.includes('network')) {
      errorMessage = 'Network error while fetching forum activity';
      errorCode = 'NETWORK_ERROR';
    }
    
    return {
      success: false,
      error: errorMessage,
      errorCode,
      originalError: error.message
    };
  }
};

/**
 * Admin moderation functions
 */

/**
 * Hide/unhide a question (admin only)
 * @param {string} questionId - The ID of the question
 * @param {string} adminId - The ID of the admin user
 * @param {boolean} hidden - Whether to hide or unhide
 * @param {string} reason - Reason for hiding
 * @returns {Promise<Object>} - Success status and data or error
 */
export const moderateQuestion = async (questionId, adminId, hidden, reason = '') => {
  try {
    if (!questionId || !adminId) {
      return { success: false, error: 'Missing required parameters' };
    }

    const questionRef = doc(db, 'forum_questions', questionId);
    const questionSnap = await getDoc(questionRef);
    
    if (!questionSnap.exists()) {
      return { success: false, error: 'Question not found' };
    }

    const updateData = {
      status: hidden ? 'hidden' : 'active',
      moderatedAt: serverTimestamp(),
      moderatedBy: adminId,
      updatedAt: serverTimestamp()
    };

    if (hidden && reason) {
      updateData.moderationReason = reason;
    }

    await updateDoc(questionRef, updateData);

    // Log moderation action
    await addDoc(collection(db, 'moderation_logs'), {
      type: 'question',
      targetId: questionId,
      action: hidden ? 'hide' : 'unhide',
      moderatorId: adminId,
      reason: reason || '',
      timestamp: serverTimestamp()
    });

    return {
      success: true,
      data: { questionId, status: updateData.status }
    };
  } catch (error) {
    console.error('Error moderating question:', error);
    return {
      success: false,
      error: error.message || 'Failed to moderate question'
    };
  }
};

/**
 * Hide/unhide a reply (admin only)
 * @param {string} replyId - The ID of the reply
 * @param {string} adminId - The ID of the admin user
 * @param {boolean} hidden - Whether to hide or unhide
 * @param {string} reason - Reason for hiding
 * @returns {Promise<Object>} - Success status and data or error
 */
export const moderateReply = async (replyId, adminId, hidden, reason = '') => {
  try {
    if (!replyId || !adminId) {
      return { success: false, error: 'Missing required parameters' };
    }

    const replyRef = doc(db, 'forum_replies', replyId);
    const replySnap = await getDoc(replyRef);
    
    if (!replySnap.exists()) {
      return { success: false, error: 'Reply not found' };
    }

    const updateData = {
      status: hidden ? 'hidden' : 'active',
      moderatedAt: serverTimestamp(),
      moderatedBy: adminId,
      updatedAt: serverTimestamp()
    };

    if (hidden && reason) {
      updateData.moderationReason = reason;
    }

    await updateDoc(replyRef, updateData);

    // Log moderation action
    await addDoc(collection(db, 'moderation_logs'), {
      type: 'reply',
      targetId: replyId,
      action: hidden ? 'hide' : 'unhide',
      moderatorId: adminId,
      reason: reason || '',
      timestamp: serverTimestamp()
    });

    return {
      success: true,
      data: { replyId, status: updateData.status }
    };
  } catch (error) {
    console.error('Error moderating reply:', error);
    return {
      success: false,
      error: error.message || 'Failed to moderate reply'
    };
  }
};

/**
 * Delete a question (admin or author)
 * @param {string} questionId - The ID of the question
 * @param {string} userId - The ID of the user requesting deletion
 * @param {string} reason - Reason for deletion (required for admin deletions of others' content)
 * @returns {Promise<Object>} - Success status and data or error
 */
export const deleteQuestion = async (questionId, userId, reason = '') => {
  try {
    console.log('Deleting forum question:', { questionId, userId, reason });

    // Validate deletion request with comprehensive permission checking
    console.log('Starting validation...');
    const validation = await validateDeletionRequest(questionId, userId, 'question', reason);
    console.log('Validation result:', validation);
    
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error,
        errorCode: validation.errorCode
      };
    }

    const { permissionData } = validation;
    const { contentData, isAdmin, isAuthor } = permissionData;

    // Use batch write for atomic operations
    const batch = writeBatch(db);
    const questionRef = doc(db, 'forum_questions', questionId);

    // Mark question as deleted instead of hard delete
    const deletionData = {
      status: 'deleted',
      deletedAt: serverTimestamp(),
      deletedBy: userId,
      deletionReason: reason || (isAdmin && !isAuthor ? 'Deleted by moderator' : 'Deleted by author'),
      updatedAt: serverTimestamp()
    };

    batch.update(questionRef, deletionData);

    // Update engagement metrics
    await updateEngagementMetricsForDeletion(questionId, 'question', contentData, batch);

    // Commit the batch transaction
    console.log('Committing batch transaction...');
    await batch.commit();
    console.log('Batch transaction committed successfully');

    // Log the deletion action (non-blocking - don't fail deletion if logging fails)
    console.log('Starting deletion logging...');
    logDeletionAction({
      type: 'question',
      targetId: questionId,
      deletedBy: userId,
      isAdmin: isAdmin && !isAuthor,
      reason: deletionData.deletionReason,
      originalData: {
        title: contentData.title,
        authorId: contentData.authorId,
        authorName: contentData.authorName,
        viewCount: contentData.viewCount || 0,
        replyCount: contentData.replyCount || 0,
        likeCount: contentData.likeCount || 0
      }
    }).catch(error => {
      // Log error but don't fail the deletion operation
      console.warn('Failed to log deletion action:', error);
    });

    console.log('Deletion completed successfully, returning success response');
    return {
      success: true,
      data: { 
        questionId, 
        status: 'deleted',
        deletedBy: userId,
        isAdminDeletion: isAdmin && !isAuthor
      }
    };
  } catch (error) {
    console.error('Error deleting question:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete question',
      errorCode: 'DELETION_FAILED'
    };
  }
};

/**
 * Delete a reply (admin or author)
 * @param {string} replyId - The ID of the reply
 * @param {string} userId - The ID of the user requesting deletion
 * @param {string} reason - Reason for deletion (required for admin deletions of others' content)
 * @returns {Promise<Object>} - Success status and data or error
 */
export const deleteReply = async (replyId, userId, reason = '') => {
  try {
    console.log('Deleting forum reply:', { replyId, userId, reason });

    // Validate deletion request with comprehensive permission checking
    const validation = await validateDeletionRequest(replyId, userId, 'reply', reason);
    
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error,
        errorCode: validation.errorCode
      };
    }

    const { permissionData } = validation;
    const { contentData, isAdmin, isAuthor } = permissionData;

    // Use batch write for atomic operations
    const batch = writeBatch(db);
    const replyRef = doc(db, 'forum_replies', replyId);

    // Mark reply as deleted instead of hard delete
    const deletionData = {
      status: 'deleted',
      deletedAt: serverTimestamp(),
      deletedBy: userId,
      deletionReason: reason || (isAdmin && !isAuthor ? 'Deleted by moderator' : 'Deleted by author'),
      updatedAt: serverTimestamp()
    };

    batch.update(replyRef, deletionData);

    // Update engagement metrics
    await updateEngagementMetricsForDeletion(replyId, 'reply', contentData, batch);

    // Commit the batch transaction
    await batch.commit();

    // Log the deletion action (non-blocking - don't fail deletion if logging fails)
    logDeletionAction({
      type: 'reply',
      targetId: replyId,
      deletedBy: userId,
      isAdmin: isAdmin && !isAuthor,
      reason: deletionData.deletionReason,
      originalData: {
        questionId: contentData.questionId,
        content: contentData.content,
        authorId: contentData.authorId,
        authorName: contentData.authorName,
        likeCount: contentData.likeCount || 0
      }
    }).catch(error => {
      // Log error but don't fail the deletion operation
      console.warn('Failed to log deletion action:', error);
    });

    return {
      success: true,
      data: { 
        replyId, 
        status: 'deleted',
        deletedBy: userId,
        isAdminDeletion: isAdmin && !isAuthor
      }
    };
  } catch (error) {
    console.error('Error deleting reply:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete reply',
      errorCode: 'DELETION_FAILED'
    };
  }
};

/**
 * Get moderation queue (flagged/reported content)
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and queue data or error
 */
export const getModerationQueue = async (options = {}) => {
  try {
    const { limit: limitCount = 20, type = 'all' } = options;
    
    const items = [];

    // Get flagged questions
    if (type === 'all' || type === 'questions') {
      const questionsQuery = query(
        collection(db, 'forum_questions'),
        where('status', 'in', ['flagged', 'reported']),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const questionsSnapshot = await getDocs(questionsQuery);
      questionsSnapshot.docs.forEach(doc => {
        items.push({
          id: doc.id,
          type: 'question',
          ...doc.data()
        });
      });
    }

    // Get flagged replies
    if (type === 'all' || type === 'replies') {
      const repliesQuery = query(
        collection(db, 'forum_replies'),
        where('status', 'in', ['flagged', 'reported']),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const repliesSnapshot = await getDocs(repliesQuery);
      repliesSnapshot.docs.forEach(doc => {
        items.push({
          id: doc.id,
          type: 'reply',
          ...doc.data()
        });
      });
    }

    // Sort by creation date
    items.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return bTime - aTime;
    });

    return {
      success: true,
      data: {
        items: items.slice(0, limitCount),
        total: items.length
      }
    };
  } catch (error) {
    console.error('Error fetching moderation queue:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch moderation queue'
    };
  }
};

/**
 * Get moderation logs
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and logs data or error
 */
export const getModerationLogs = async (options = {}) => {
  try {
    const { limit: limitCount = 50, moderatorId = null } = options;
    
    let logsQuery = collection(db, 'moderation_logs');
    const queryConstraints = [orderBy('timestamp', 'desc'), limit(limitCount)];
    
    if (moderatorId) {
      queryConstraints.unshift(where('moderatorId', '==', moderatorId));
    }
    
    logsQuery = query(logsQuery, ...queryConstraints);
    
    const snapshot = await getDocs(logsQuery);
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      data: { logs }
    };
  } catch (error) {
    console.error('Error fetching moderation logs:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch moderation logs'
    };
  }
};

/**
 * Helper functions
 */

/**
 * Generate search keywords from title and content
 * @param {string} title - Question title
 * @param {string} content - Question content
 * @returns {Array} - Array of search keywords
 */
const generateSearchKeywords = (title, content) => {
  const text = `${title} ${content}`.toLowerCase();
  const words = text.match(/\b\w+\b/g) || [];
  
  // Remove common stop words and short words
  const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should'];
  
  const keywords = words
    .filter(word => word.length > 2 && !stopWords.includes(word))
    .slice(0, 20); // Limit to 20 keywords
  
  return [...new Set(keywords)]; // Remove duplicates
};

/**
 * Apply search filtering to questions
 * @param {Array} questions - Array of questions to filter
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} - Filtered questions
 */
const applySearchFilter = (questions, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return questions;
  }
  
  const term = searchTerm.toLowerCase();
  const searchWords = term.split(/\s+/).filter(word => word.length > 0);
  
  return questions.filter(question => {
    // Create searchable text from question data
    const searchableText = [
      question.title,
      question.content,
      question.authorName,
      ...(question.tags || []),
      ...(question.searchKeywords || [])
    ].filter(Boolean).join(' ').toLowerCase();
    
    // Check if all search words are found in the searchable text
    return searchWords.every(word => searchableText.includes(word));
  });
};

/**
 * Update user forum statistics
 * @param {string} userId - The ID of the user
 * @param {Object} updates - The stats to update
 * @returns {Promise<void>}
 */
const updateUserForumStats = async (userId, updates) => {
  try {
    const userRef = doc(db, 'user_profiles', userId);
    const updateData = {
      [`forumStats.lastActivity`]: serverTimestamp(),
      lastUpdated: serverTimestamp()
    };
    
    // Add specific stat updates
    Object.keys(updates).forEach(key => {
      updateData[`forumStats.${key}`] = updates[key];
    });
    
    await updateDoc(userRef, updateData);
  } catch (error) {
    console.warn('Failed to update user forum stats:', error);
    // Don't throw error as this is not critical
  }
};

/**
 * Admin moderation functions
 */

/**
 * Update question or reply status (admin only)
 * @param {string} targetType - 'question' or 'reply'
 * @param {string} targetId - The ID of the question or reply
 * @param {string} status - The new status
 * @param {string} adminId - The ID of the admin user
 * @returns {Promise<Object>} - Success status or error
 */
export const moderateContent = async (targetType, targetId, status, adminId) => {
  try {
    if (!['question', 'reply'].includes(targetType)) {
      return { success: false, error: 'Invalid target type' };
    }
    
    if (!['active', 'hidden', 'deleted'].includes(status)) {
      return { success: false, error: 'Invalid status' };
    }
    
    const targetCollection = targetType === 'question' ? 'forum_questions' : 'forum_replies';
    const targetRef = doc(db, targetCollection, targetId);
    
    const updateData = {
      status,
      moderatedBy: adminId,
      moderatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(targetRef, updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error moderating content:', error);
    return {
      success: false,
      error: error.message || 'Failed to moderate content'
    };
  }
};